'use client';

import { connectUseCaseHref } from '@/@connect/use-cases';
import { connectUseCaseOf } from '@/@connect/conversation-use-case';
import { ScrollToTopButton } from '@/@studio-ai/components/scroll-to-top-button';
import PulseLoader from '@/@studio/components/PulseLoader';
import { useSubsidiaryFilterOptional } from '@/@subsidiary/context';
import { StudioRoutes } from '@/app/routes';
import { RenameModal } from '@/components/ui/organisms/RenameModal';
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFeasibilityFiles } from '@/@feasibility-dani/components/chat/FeasibilityLaunch';
import { useFeatureFlags } from '@/@feature-flags/hooks/useFeatureFlags';
import { hasFeasibilityRules, nextReplies } from '@/lib/feasibility/dani/primer-marks';
import { useStudioAI, useStudioAIViewAs } from '../context';
import { useChatPageCommon } from '../hooks/useChatPageCommon';
import type { ChatConversationProps } from '../props';
import { VideoStudyChip } from '../reels/components/VideoStudyChip';
import { ChatInput } from './chat-input';
import { useElementHeight } from '../hooks/useElementHeight';
import { ChatMessages } from './chat-messages';
import { MasqueradeBanner } from './client-masquerade';
import { ConversationTopBar } from './conversation-top-bar';
import { DarkTooltip } from './dark-tooltip';
import { FreemiumLimitModal } from './freemium-limit-modal';
import { ShareModal } from './history/share-modal';
import { PlanSidebar } from './planner-mode/plan-sidebar';
import { StudioAICreditBanners } from './studio-ai-credit-banners';
import { isAwaitingReply } from '../utils/message-utils';
import type { ViewAsConversationMismatch } from '../utils/view-as-dani';

/**
 * A conversation run as another client, or as the staff member, than the tab
 * is acting as (staff View-as, M4S-5670 §4). The composer is locked; the way on
 * is a recorded View-as session as that client, or a new chat.
 */
function ViewAsMismatchPanel({
    mismatch,
    onNewChat,
}: {
    mismatch: ViewAsConversationMismatch;
    onNewChat: () => void;
}) {
    const subsidiary = useSubsidiaryFilterOptional();
    const [isSwitching, setIsSwitching] = useState(false);
    const runAs = mismatch.sfAccountId ? (mismatch.orgName ?? mismatch.sfAccountId) : null;

    const handleViewAs = async () => {
        if (!subsidiary || !mismatch.sfAccountId) return;
        setIsSwitching(true);
        // The server resolves the org from the SF id; the tab reloads as that client.
        const started = await subsidiary.startViewAs({ sfAccountId: mismatch.sfAccountId });
        if (!started) setIsSwitching(false);
    };

    const handleClear = async () => {
        if (!subsidiary) return;
        setIsSwitching(true);
        await subsidiary.stopViewAs('clear');
    };

    return (
        <div className="border-b border-warning-200 bg-warning-50 px-4 py-2" role="status">
            <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-2">
                <div className="flex items-center gap-2 text-small text-warning-800">
                    <Icon icon="lucide:lock" width={14} />
                    <span>
                        This conversation was run as {runAs ? <strong>{runAs}</strong> : 'you'}.
                    </span>
                </div>
                {runAs ? (
                    <Button
                        size="sm"
                        variant="flat"
                        color="warning"
                        isLoading={isSwitching}
                        isDisabled={!subsidiary}
                        onPress={() => void handleViewAs()}
                        startContent={!isSwitching && <Icon icon="lucide:hat-glasses" width={14} />}
                    >
                        View as {runAs}
                    </Button>
                ) : subsidiary?.viewAsSessionId ? (
                    <Button
                        size="sm"
                        variant="flat"
                        color="warning"
                        isLoading={isSwitching}
                        onPress={() => void handleClear()}
                    >
                        Clear View as
                    </Button>
                ) : null}
                <Button
                    size="sm"
                    variant="light"
                    isDisabled={isSwitching}
                    onPress={onNewChat}
                    startContent={<Icon icon="lucide:plus" width={14} />}
                >
                    New chat
                </Button>
            </div>
        </div>
    );
}

/** The feasibility check a conversation is about, kept above the composer (internal only). */
/** A survey being written or followed in the chat: Builder's cards, not this button, check it. */
const SURVEY_BLOCK = /```survey-(start|change|draft|next|status)\b/;

/** Check feasibility from any conversation, building the check from its context (internal only). */
const FeasibilityLaunch = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilityLaunch').then(
            (m) => m.FeasibilityLaunch,
        ),
    { ssr: false },
);
/** Build a crosstab from the conversation (M4S-5777 X149); renders nothing unless allowed. */
const CrosstabLaunch = dynamic(
    () =>
        import('@/app/(private)/ai/components/Crosstabs/dani/CrosstabLaunch').then(
            (m) => m.CrosstabLaunch,
        ),
    { ssr: false },
);
const FeasibilityChatBar = dynamic(
    () =>
        import('@/@feasibility-dani/components/chat/FeasibilityChatBar').then(
            (m) => m.FeasibilityChatBar,
        ),
    { ssr: false },
);

/** The Builder survey a conversation is about, kept above the composer (anyone with Builder). */
const BuilderChatBar = dynamic(
    () => import('@/@builder/components/chat/BuilderChatBar').then((m) => m.BuilderChatBar),
    { ssr: false },
);

/**
 * Active conversation view for /dani/[conversationId].
 * Renders the conversation top bar, message list, planner status, and chat input.
 */
export function ChatConversation({ className = '' }: ChatConversationProps) {
    const params = useParams<{ conversationId: string }>();
    const conversationId = params?.conversationId ?? null;
    // Read once per conversation: the record is written before this page opens.
    const startedFromUseCase = useMemo(() => connectUseCaseOf(conversationId), [conversationId]);

    const {
        router,
        messages,
        conversations,
        startNewConversation,
        duplicateConversation,
        renameConversation,
        isSharedView,
        isUnaffiliated,
        isFreemium,
        isInternal,
        masqueradeClient,
        setMasqueradeClient,
        isMasqueradeLocked,
        isGlobalViewAsActive,
        limitModalOpen,
        setLimitModalOpen,
        liveUsage,
        sharedChatInputProps,
        sharedChatMessagesOptionProps,
    } = useChatPageCommon({ conversationId });

    const {
        planProgress,
        organizationUsers,
        shareConversation,
        getConversationShares,
        revokeShare,
        isCurrentConversationOwner,
        userSession,
        seededChat,
    } = useStudioAI();
    const { conversationMismatch } = useStudioAIViewAs();
    // An insight card's chat, until the reader's first send: it reads from the
    // top, and the card's follow-up question waits in the composer
    // (docs/pdlc/insights-dive-deep.md §3.3).
    const isFreshSeed = seededChat?.conversationId === conversationId && messages.length <= 2;
    // A starting sentence the feasibility card puts in the composer.
    const [feasibilityDraft, setFeasibilityDraft] = useState<string | undefined>(undefined);
    // The composer's recommended next step, from the survey or the check in view.
    const [builderSuggestion, setBuilderSuggestion] = useState<string | null>(null);
    const [feasibilitySuggestion, setFeasibilitySuggestion] = useState<string | null>(null);
    // The composer refills only when the draft changes, so the same starter twice differs by a space.
    const putDraft = useCallback(
        (text: string) => setFeasibilityDraft((prev) => (prev === text ? `${text} ` : text)),
        [],
    );
    // A check is in view: its card replaces the Check feasibility button.
    const [aboutCheck, setAboutCheck] = useState(false);
    // A survey in view takes its feasibility check through its own next step, in order.
    const [surveyInView, setSurveyInView] = useState(false);
    // A survey written in this chat but not saved yet: its own card runs its check.
    const surveyInChat = useMemo(
        () =>
            messages.some((m) =>
                SURVEY_BLOCK.test(
                    typeof m.content === 'string' ? m.content : JSON.stringify(m.content ?? ''),
                ),
            ),
        [messages],
    );
    // DANI is still answering: nothing offers a step its reply may take (UAT F55).
    const replying = useMemo(() => isAwaitingReply(messages), [messages]);
    // A feasibility chat (a check in view, or DANI already has the feasibility
    // rules): the composer's paperclip reads files for the check, several at once.
    const { hasFlag } = useFeatureFlags();
    const feasibilityFiles = useFeasibilityFiles(conversationId ?? '', messages);
    const feasibilityChat =
        Boolean(isInternal && conversationId && hasFlag('feasibility-dani')) &&
        (aboutCheck || hasFeasibilityRules(messages));
    // What DANI suggested the person say next, from its latest finished reply.
    const suggestions = useMemo(() => {
        const last = [...messages].reverse().find((m) => m.role === 'assistant');
        return last && !last.isStreaming ? nextReplies(last.content) : [];
    }, [messages]);
    const composerDraft =
        feasibilityDraft ?? (isFreshSeed ? seededChat?.draft || undefined : undefined);
    const showPlanSidebar =
        !!planProgress &&
        (planProgress.steps?.length ?? 0) > 0 &&
        sharedChatInputProps.smartRoutingEnabled;

    // Share modal state
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    // Rename modal state
    const [isRenameOpen, setIsRenameOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [localTitleOverride, setLocalTitleOverride] = useState<string | null>(null);

    useEffect(() => {
        setLocalTitleOverride(null);
    }, [conversationId]);

    // Scroll state
    const scrollToBottomRef = useRef<(() => void) | null>(null);
    const scrollToTopRef = useRef<(() => void) | null>(null);
    const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);
    const [showScrollToTopButton, setShowScrollToTopButton] = useState(false);

    const { ref: footerRef, height: footerHeight } = useElementHeight<HTMLDivElement>(90);

    const handleScrollStateChange = useCallback((isAtTop: boolean, isAtBottom: boolean) => {
        setShowScrollToTopButton(!isAtTop);
        setShowScrollToBottomButton(!isAtBottom);
    }, []);

    const handleScrollToBottom = useCallback(() => {
        scrollToBottomRef.current?.();
    }, []);

    const handleScrollToTop = useCallback(() => {
        scrollToTopRef.current?.();
    }, []);

    const currentConversationTitle = useMemo(() => {
        if (!conversationId) return null;
        const conv = conversations.find((c) => c.conversationId === conversationId);
        return conv?.title || null;
    }, [conversationId, conversations]);

    const openRenameModal = useCallback(() => {
        setRenameValue(currentConversationTitle || '');
        setIsRenameOpen(true);
    }, [currentConversationTitle]);

    const handleRenameConfirm = useCallback(async () => {
        if (!conversationId || !renameValue.trim()) return;
        try {
            await renameConversation(conversationId, renameValue.trim());
            setLocalTitleOverride(renameValue.trim());
        } catch {
            // Error handling in context
        }
        setIsRenameOpen(false);
    }, [conversationId, renameValue, renameConversation]);

    return (
        <div className={`relative flex h-full bg-[#FAFAFA] ${className}`}>
            {conversationId && isCurrentConversationOwner && isShareModalOpen && (
                <ShareModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    conversationId={conversationId}
                    conversationTitle={
                        localTitleOverride || currentConversationTitle || 'Conversation'
                    }
                    organizationUsers={organizationUsers}
                    onShare={shareConversation}
                    onGetShares={getConversationShares}
                    onRevokeShare={revokeShare}
                />
            )}

            <RenameModal
                isOpen={isRenameOpen}
                value={renameValue}
                title="Rename Conversation"
                onChange={setRenameValue}
                onClose={() => setIsRenameOpen(false)}
                onConfirm={handleRenameConfirm}
            />

            {isFreemium && (
                <FreemiumLimitModal
                    isOpen={limitModalOpen}
                    onOpenChange={setLimitModalOpen}
                    liveUsage={liveUsage}
                />
            )}

            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                {!isSharedView && !isUnaffiliated && (
                    <ConversationTopBar
                        backUrl={StudioRoutes.STUDIO_AI_HISTORY}
                        title={localTitleOverride || currentConversationTitle}
                        onNewChat={() => startNewConversation()}
                        onRenameTitle={conversationId ? openRenameModal : undefined}
                        extraActions={
                            <>
                                {startedFromUseCase && (
                                    <DarkTooltip
                                        content={`Start ${startedFromUseCase.name} again for another brand or time period`}
                                        size="sm"
                                        delay={400}
                                    >
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="max-sm:w-8 max-sm:min-w-8 max-sm:px-0"
                                            aria-label={`Run ${startedFromUseCase.name} again`}
                                            startContent={
                                                <Icon icon="lucide:rotate-ccw" width={14} />
                                            }
                                            onPress={() =>
                                                startNewConversation(
                                                    connectUseCaseHref(startedFromUseCase.id),
                                                )
                                            }
                                        >
                                            <span className="text-small max-sm:hidden">
                                                Run {startedFromUseCase.name} again
                                            </span>
                                        </Button>
                                    </DarkTooltip>
                                )}
                                {conversationId && isCurrentConversationOwner ? (
                                    <DarkTooltip content="Share conversation" size="sm" delay={400}>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="max-sm:w-8 max-sm:min-w-8 max-sm:px-0"
                                            aria-label="Share conversation"
                                            startContent={<Icon icon="lucide:share-2" width={14} />}
                                            onPress={() => setIsShareModalOpen(true)}
                                        >
                                            <span className="text-small max-sm:hidden">Share</span>
                                        </Button>
                                    </DarkTooltip>
                                ) : null}
                            </>
                        }
                    />
                )}

                {isInternal && masqueradeClient && !isGlobalViewAsActive && (
                    <MasqueradeBanner
                        client={masqueradeClient}
                        onStop={() => setMasqueradeClient(null)}
                        isLocked={isMasqueradeLocked}
                    />
                )}

                {conversationMismatch && !isSharedView && (
                    <ViewAsMismatchPanel
                        mismatch={conversationMismatch}
                        onNewChat={() => startNewConversation()}
                    />
                )}

                {!isSharedView && (
                    <div className="shrink-0 px-4 pt-2">
                        <div className="mx-auto max-w-3xl">
                            <StudioAICreditBanners placement="top" />
                        </div>
                    </div>
                )}

                {messages.length > 0 ? (
                    <div className="relative flex min-h-0 w-full flex-1 flex-col">
                        <ChatMessages
                            messages={messages}
                            onScrollStateChange={handleScrollStateChange}
                            scrollToBottomRef={scrollToBottomRef}
                            scrollToTopRef={scrollToTopRef}
                            startAtTop={isFreshSeed}
                            style={{ paddingBottom: footerHeight }}
                            {...sharedChatMessagesOptionProps}
                        />

                        {/* Scroll to bottom is not defined here because it should be bound to the chat input when it resizes */}
                        <ScrollToTopButton
                            show={showScrollToTopButton}
                            onPress={handleScrollToTop}
                        />
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 items-center justify-center">
                        <PulseLoader title="conversation" />
                    </div>
                )}

                {isSharedView ? (
                    <div className="border-t border-divider bg-secondary-50 p-3">
                        <div className="mx-auto flex max-w-3xl items-center justify-center gap-4 text-small text-secondary-600">
                            {!isUnaffiliated && (
                                <DarkTooltip
                                    content="Manage your conversation history"
                                    size="sm"
                                    delay={400}
                                >
                                    <Button
                                        isIconOnly
                                        aria-label="Manage conversation history"
                                        variant="bordered"
                                        radius="full"
                                        size="sm"
                                        className="mb-2 h-9 w-9 min-w-9 border-default-200 text-default-500 data-[hover=true]:bg-default-100 data-[hover=true]:text-default-700"
                                        onPress={() => router.push(StudioRoutes.STUDIO_AI_HISTORY)}
                                    >
                                        <Icon icon="lucide:clock" width={16} />
                                    </Button>
                                </DarkTooltip>
                            )}
                            <div className="flex items-center gap-1">
                                <Icon icon="lucide:eye" width={16} />
                                <span className="text-sm text-secondary-600">
                                    You are viewing a shared conversation (read-only)
                                </span>
                            </div>
                            <DarkTooltip
                                content="Duplicate conversation and start a new chat"
                                size="sm"
                                delay={400}
                            >
                                <Button
                                    isIconOnly
                                    aria-label="Duplicate conversation and start a new chat"
                                    variant="bordered"
                                    radius="full"
                                    size="sm"
                                    className="mb-2 h-9 w-9 min-w-9 border-default-200 text-default-500 data-[hover=true]:bg-default-100 data-[hover=true]:text-default-700"
                                    onPress={() =>
                                        conversationId
                                            ? duplicateConversation(conversationId)
                                            : startNewConversation()
                                    }
                                >
                                    <Icon icon="lucide:copy" width={16} height={16} />
                                </Button>
                            </DarkTooltip>
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            ref={footerRef}
                            className="pointer-events-none absolute bottom-0 z-20 w-full overflow-visible bg-gradient-to-t from-background via-background/80 to-transparent px-4 pb-4 pt-2"
                        >
                            <div className="pointer-events-auto mx-auto max-w-[825px] space-y-2">
                                <StudioAICreditBanners placement="bottom" />
                                <VideoStudyChip conversationId={conversationId ?? null} />
                                <ChatInput
                                    {...sharedChatInputProps}
                                    // Plan mode has no part in a feasibility chat: no switch, and a
                                    // switch left on elsewhere does not send this chat's messages to it.
                                    {...(feasibilityChat
                                        ? {
                                              showSmartRoutingToggle: false,
                                              smartRoutingEnabled: false,
                                          }
                                        : {})}
                                    aboveInput={
                                        conversationId && (
                                            <>
                                                <BuilderChatBar
                                                    conversationId={conversationId}
                                                    messageCount={messages.length}
                                                    onDraft={putDraft}
                                                    onSuggest={setBuilderSuggestion}
                                                    onSurvey={setSurveyInView}
                                                />
                                                {isInternal && (
                                                    <>
                                                        <FeasibilityChatBar
                                                            conversationId={conversationId}
                                                            messageCount={messages.length}
                                                            onDraft={putDraft}
                                                            suggestions={suggestions}
                                                            onSuggest={setFeasibilitySuggestion}
                                                            onCheck={setAboutCheck}
                                                        />
                                                        <FeasibilityLaunch
                                                            conversationId={conversationId}
                                                            messages={messages}
                                                            hidden={
                                                                aboutCheck ||
                                                                surveyInView ||
                                                                surveyInChat ||
                                                                replying
                                                            }
                                                        />
                                                        <CrosstabLaunch
                                                            conversationId={conversationId}
                                                            messages={messages}
                                                        />
                                                    </>
                                                )}
                                            </>
                                        )
                                    }
                                    fileReader={
                                        feasibilityChat
                                            ? {
                                                  accept: feasibilityFiles.accept,
                                                  onFiles: (files) =>
                                                      void feasibilityFiles.readFiles(files),
                                                  busy: feasibilityFiles.reading,
                                              }
                                            : undefined
                                    }
                                    initialMessage={composerDraft}
                                    suggestion={
                                        sharedChatInputProps.isLoading ||
                                        sharedChatInputProps.placeholder
                                            ? undefined
                                            : (suggestions[0] ??
                                              builderSuggestion ??
                                              feasibilitySuggestion ??
                                              undefined)
                                    }
                                    width={825}
                                    height={100}
                                    onScrollToBottom={handleScrollToBottom}
                                    showScrollButton={
                                        showScrollToBottomButton && messages.length > 0
                                    }
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {showPlanSidebar && planProgress && <PlanSidebar payload={planProgress} />}
        </div>
    );
}
